/**
 * Single source of truth for Service-module field-visibility config.
 *
 * Mirrors the backend default tree in `service_config_routes.py`. Keeping
 * them in lock-step means (a) new tenants see all features by default, and
 * (b) legacy tenants who haven't re-saved get sane fallbacks in the UI.
 */

// Fixed mapping between code-side tab IDs (kebab-case, in URL) and the
// visibility config's keys (snake_case). Reconciliation deliberately omitted.
export const REPORT_KEY_MAP = {
  'vehicle-inwards':  'vehicle_inwards',
  'vehicle-outwards': 'vehicle_outwards',
  'technician':       'technician_wise',
  'job-status':       'job_status',
  'service-head':     'service_head_report',
  'job-type':         'job_type_report',
  'supervisor':       'supervisor_wise',
};

const REPORTS_DEFAULT_FILTERS = {
  reg_no: true, party_name: true, mobile: true, supervisor: true,
  technician: true, job_type: true, service_head: true, status: true,
  show_pdi: true, show_delivered: true,
};

const REPORTS_DEFAULT_PRESETS = {
  today: true, yesterday: true, wtd: true, mtd: true,
  qtd: true, ytd: true, custom: true,
};

const mkReportDefaults = () => ({
  filters: { ...REPORTS_DEFAULT_FILTERS },
  date_presets: { ...REPORTS_DEFAULT_PRESETS },
});

export const DEFAULT_FIELD_VISIBILITY = {
  job_cards: {
    columns: {
      doc_type: true, party_name: true, reg_no: true, model: true,
      service_head: true, job_type: true, supervisor: true, technician: true,
      vehicle_in_date: true, vehicle_out_date: true, chassis_no: true,
      mobile_no_1: true, booking_no: true, customer_type: true,
      customer_voice: true, kms: true, color: true,
    },
    filters: {
      reg_no: true, party_name: true, mobile: true, supervisor: true,
      technician: true, job_type: true, service_head: true, status: true,
      show_pdi: true, show_delivered: true, sla_filter: true, date_range: true,
    },
  },
  reports: {
    vehicle_inwards:     mkReportDefaults(),
    vehicle_outwards:    mkReportDefaults(),
    technician_wise:     mkReportDefaults(),
    job_status:          mkReportDefaults(),
    service_head_report: mkReportDefaults(),
    job_type_report:     mkReportDefaults(),
    supervisor_wise:     mkReportDefaults(),
  },
};

/** Deep-merge overlay into base; overlay wins, nested dicts recurse. */
export function deepMergeVisibility(base, overlay) {
  if (!overlay || typeof overlay !== 'object') return { ...base };
  const out = { ...base };
  for (const k of Object.keys(overlay)) {
    const v = overlay[k];
    if (v && typeof v === 'object' && !Array.isArray(v) && out[k] && typeof out[k] === 'object') {
      out[k] = deepMergeVisibility(out[k], v);
    } else {
      out[k] = v;
    }
  }
  return out;
}
